import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { logisticsNotificationPath } from "./notificationRoutes";
import axios from "axios";
import { toast } from "sonner";
import { getBaseUrl, _getCookieValue, _getConfig, getUserData } from "../Helper/commonFunctions_v2";
import notificationToast from "../util/Notification/NotificationToast";
import { canUseAccessReviewHr } from "../components/AccessReview/accessReviewShared";

// Example notifications in backend shape (keep `timestamp` as epoch ms).
// eslint-disable-next-line no-unused-vars
const DEFAULT_NOTIFICATIONS = [
    {
        id: 6,
        source_type: "qc",
        source_id: "3029",
        priority: "LOW",
        heading: "Qc completed",
        text: "qc has been completed with status : SUCCESS",
        is_read: false,
        timestamp: Date.now() - 4 * 60 * 60 * 1000,
        primary_action: {
            id: 1,
            displayType: "NOTIFICATION",
            label: "",
            icon: null,
            actionType: "REDIRECT",
            url: "https://devportal.geniemode.com/quality-check/reportfabricV2/3029",
            position: "PRIMARY",
            params: {},
            apiType: "",
        },
        actions: [
            {
                id: 1,
                displayType: "BUTTON",
                label: "Accept",
                icon: null,
                actionType: "API",
                url: "https://devportal.geniemode.com/testsecondary",
                position: "SECONDARY",
                params: { test: true },
                apiType: "GET",
            },
            {
                id: 2,
                displayType: "BUTTON",
                label: "Reject",
                icon: null,
                actionType: "API",
                url: "https://devportal.geniemode.com/testtertiary",
                position: "TERTIARY",
                params: { test: false },
                apiType: "POST",
            },
        ],
    },
    {
        id: 7,
        source_type: "finance",
        source_id: "1001",
        priority: "HIGH",
        heading: "Payment pending",
        text: "Invoice #1001 is pending approval.",
        is_read: false,
        timestamp: Date.now() - 2 * 60 * 60 * 1000,
        primary_action: null,
        actions: [],
    },
    {
        id: 8,
        source_type: "vp-manager",
        source_id: "7781",
        priority: "MEDIUM",
        heading: "Deck ready for review",
        text: "The Q4 planning deck is ready for your feedback.",
        is_read: false,
        timestamp: Date.now() - 6 * 60 * 60 * 1000,
        primary_action: null,
        actions: [],
    },
    {
        id: 9,
        source_type: "factory",
        source_id: "4412",
        priority: "LOW",
        heading: "Production started",
        text: "Factory F-4412 has started production for PO #9932.",
        is_read: true,
        timestamp: Date.now() - 30 * 60 * 60 * 1000,
        primary_action: null,
        actions: [],
    },
    {
        id: 10,
        source_type: "logistics",
        source_id: "5290",
        priority: "MEDIUM",
        heading: "Pickup scheduled",
        text: "Pickup for shipment 5290 is scheduled for tomorrow.",
        is_read: false,
        timestamp: Date.now() - 20 * 60 * 60 * 1000,
        primary_action: null,
        actions: [],
    },
    {
        id: 11,
        source_type: "finance",
        source_id: "2045",
        priority: "LOW",
        heading: "Credit note approved",
        text: "Credit note 2045 has been approved and will be adjusted in the next cycle.",
        is_read: true,
        timestamp: Date.now() - 3 * 24 * 60 * 60 * 1000,
        primary_action: null,
        actions: [],
    },
    {
        id: 12,
        source_type: "qc",
        source_id: "3055",
        priority: "HIGH",
        heading: "QC failed",
        text: "qc for lot 3055 failed with status : RECHECK REQUIRED.",
        is_read: false,
        timestamp: Date.now() - 50 * 60 * 60 * 1000,
        primary_action: null,
        actions: [],
    },
    {
        id: 13,
        source_type: "logistics",
        source_id: "6102",
        priority: "MEDIUM",
        heading: "Documents uploaded",
        text: "All export documents for shipment 6102 are now available.",
        is_read: true,
        timestamp: Date.now() - 5 * 24 * 60 * 60 * 1000,
        primary_action: null,
        actions: [],
    },
    {
        id: 14,
        source_type: "factory",
        source_id: "4477",
        priority: "HIGH",
        heading: "Line on hold",
        text: "Production line for style 4477 is on hold due to material issue.",
        is_read: false,
        timestamp: Date.now() - 12 * 60 * 60 * 1000,
        primary_action: null,
        actions: [],
    },
];

/**
 * Filters out duplicate notifications based on ID.
 * Uses Set for O(1) lookup performance.
 *
 * @param {Array} newNotifications - New notifications to add
 * @param {Array} existingNotifications - Current notifications in state
 * @returns {Array} Only unique notifications from newNotifications
 */
const deduplicateNotifications = (newNotifications, existingNotifications) => {
    if (!Array.isArray(newNotifications) || newNotifications.length === 0) {
        return [];
    }

    if (!Array.isArray(existingNotifications) || existingNotifications.length === 0) {
        return newNotifications;
    }

    // Create Set of existing IDs for O(1) lookups
    const existingIds = new Set(existingNotifications.map((n) => n.id));

    // Filter out notifications that already exist
    return newNotifications.filter((notification) => {
        if (notification.id === null || notification.id === undefined) {
            console.warn("Notification without ID detected:", notification);
            return true; // Keep it to avoid data loss
        }
        return !existingIds.has(notification.id);
    });
};

const NotificationContext = createContext();

export { NotificationContext };

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error("useNotification must be used within a NotificationProvider");
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isWsConnected, setIsWsConnected] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const connectionAttemptsRef = useRef(0);
    const notificationAudioRef = useRef(null);
    const [isPanelOpen, setIsPanelOpen] = useState(false);

    // Play notification sound
    const playNotificationSound = useCallback(() => {
        try {
            if (!notificationAudioRef.current) {
                notificationAudioRef.current = new Audio("/sounds/notification_tone.mp3");
            }
            // Reset audio to start if it's already playing
            notificationAudioRef.current.currentTime = 0;
            notificationAudioRef.current.play().catch((error) => {
                console.error("Error playing notification sound:", error);
            });
        } catch (error) {
            console.error("Error initializing notification sound:", error);
        }
    }, []);

    // Open and close notification panel
    const openPanel = useCallback(() => {
        setIsPanelOpen(true);
        toast.dismiss();
    }, []);

    const closePanel = useCallback(() => {
        setIsPanelOpen(false);
    }, []);

    // Fetch unread count from API
    const fetchUnreadCount = useCallback(async () => {
        // Check if user is authenticated before making API call
        const config = _getConfig();
        const token = config?.headers?.authorization || localStorage.getItem("token") || sessionStorage.getItem("token") || _getCookieValue("token");
        const userId = getUserData()?.id;

        if (!token || !userId) {
            console.log("No token or user ID available, skipping unread count fetch");
            return;
        }

        try {
            const response = await axios.get(`${getBaseUrl("notes")}/api/v1/notification/unReadCount`);
            if (typeof response.data === "number") {
                setUnreadCount(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch unread count:", error);
            // If it's a 401 error, stop trying to fetch
            if (error?.response?.status === 401) {
                console.log("Unauthorized - stopping notification fetches");
            }
        }
    }, []);

    // Fetch notifications from API
    const fetchNotifications = useCallback(async (page = 1, appendResults = false) => {
        // Check if user is authenticated before making API call
        const config = _getConfig();
        const token = config?.headers?.authorization || localStorage.getItem("token") || sessionStorage.getItem("token") || _getCookieValue("token");
        const userId = getUserData()?.id;

        if (!token || !userId) {
            console.log("No token or user ID available, skipping notifications fetch");
            return;
        }

        if (appendResults) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
        }

        try {
            const response = await axios.get(`${getBaseUrl("notes")}/api/v1/notification`, {
                params: {
                    page: page,
                    size: 20,
                },
            });
            // throw new Error("Test error handling"); // Remove or comment out this line in production

            if (response.data && Array.isArray(response.data.content)) {
                const normalizedNotifications = response.data.content.map((notification) => ({
                    ...notification,
                    timestamp: notification.timestamp || notification.created_at || notification.createdAt || notification.updatedAt || notification.updated_at || Date.now(),
                }));

                if (appendResults) {
                    setNotifications((prev) => {
                        const uniqueNewNotifications = deduplicateNotifications(normalizedNotifications, prev);
                        return [...prev, ...uniqueNewNotifications];
                    });
                } else {
                    setNotifications(normalizedNotifications);
                }

                // Update pagination state
                setCurrentPage(response.data.currentPage ?? page);
                setTotalPages(response.data.totalPages ?? 0);
                setTotalItems(response.data.totalItems ?? normalizedNotifications.length);
                setHasMore((response.data.currentPage ?? page) < (response.data.totalPages ?? 1));
            }
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
            // If it's a 401 error, stop trying to fetch and don't show error toast
            if (error?.response?.status === 401) {
                console.log("Unauthorized - stopping notification fetches");
            } else if (!appendResults) {
                toast.error("Failed to load notifications");
            }
            // setNotifications(DEFAULT_NOTIFICATIONS);
        } finally {
            if (appendResults) {
                setIsLoadingMore(false);
            } else {
                setIsLoading(false);
            }
        }
    }, []);

    // Mark notification as read
    const markAsRead = useCallback(async (notificationId) => {
        // Check if user is authenticated before making API call
        const config = _getConfig();
        const token = config?.headers?.authorization || localStorage.getItem("token") || sessionStorage.getItem("token") || _getCookieValue("token");
        const userId = getUserData()?.id;

        if (!token || !userId) {
            console.log("No token or user ID available, skipping mark as read");
            return;
        }

        try {
            await axios.put(`${getBaseUrl("notes")}/api/v1/notification/${notificationId}`, { is_read: true });

            // Update local state
            setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n)));

            // Decrease unread count
            setUnreadCount((prev) => Math.max(0, prev - 1));
        } catch (error) {
            console.error("Failed to mark notification as read:", error);
            if (error?.response?.status === 401) {
                console.log("Unauthorized - stopping notification operations");
            }
        }
    }, []);

    // Mark all notifications as read
    const markAllAsRead = useCallback(async () => {
        // Check if user is authenticated before making API call
        const config = _getConfig();
        const token = config?.headers?.authorization || localStorage.getItem("token") || sessionStorage.getItem("token") || _getCookieValue("token");
        const userId = getUserData()?.id;

        if (!token || !userId) {
            console.log("No token or user ID available, skipping mark all as read");
            return;
        }

        try {
            await axios.put(`${getBaseUrl("notes")}/api/v1/notification/readAll`);
            setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
            setUnreadCount(0);
            toast.success("All notifications marked as read");
        } catch (error) {
            console.error("Failed to mark all as read:", error);
            if (error?.response?.status === 401) {
                console.log("Unauthorized - stopping notification operations");
            } else {
                toast.error("Failed to mark all as read");
            }
        }
    }, []);

    // Fetch more notifications for infinite scroll
    const fetchMoreNotifications = useCallback(async () => {
        if (isLoadingMore || !hasMore) {
            return;
        }

        const nextPage = currentPage + 1;
        await fetchNotifications(nextPage, true);
    }, [currentPage, hasMore, isLoadingMore, fetchNotifications]);

    // Execute action (redirect or API call)
    const executeAction = useCallback(async (action) => {
        if (!action) return;

        const { actionType, url, apiType, params, label } = action;

        if (actionType === "REDIRECT" && url) {
            try {
                window.open(url, "_blank");
            } catch {
                window.open(url, "_blank");
            }
        } else if (actionType === "API" && url) {
            const method = (apiType || "GET").toLowerCase();
            const config = { url, method };

            if (method === "get" || method === "delete") {
                config.params = params || {};
            } else {
                config.data = params || {};
            }

            const actionLabel = label || "Action";

            toast.promise(axios(config), {
                loading: `Processing ${actionLabel}...`,
                success: `${actionLabel} completed successfully`,
                error: (err) => {
                    const errorMessage = err?.response?.data?.message || err?.message || "Something went wrong";
                    return `${actionLabel} failed: ${errorMessage}`;
                },
            });
        }
    }, []);

    // Handle notification click
    const handleNotificationClick = useCallback(
        async (item) => {
            if (!item.is_read) {
                await markAsRead(item.id);
            }

            // Notes list returns actions:[] for ACCESS_REVIEW — route from source_type/source_id.
            const sourceType = String(item.source_type || "")
                .toUpperCase()
                .replace(/-/g, "_");
            if (sourceType === "ACCESS_REVIEW" && item.source_id != null && item.source_id !== "") {
                const hasAction = item.primary_action || (Array.isArray(item.actions) && item.actions.length > 0);
                if (!hasAction) {
                    const path = canUseAccessReviewHr() ? `/access-review/hr/${item.source_id}` : "/access-review/my";
                    window.location.assign(path);
                    return;
                }
            }

            // GM-6538: logistics hop bells without an action go to the invoice / the Hops board.
            const hasAnyAction = item.primary_action || (Array.isArray(item.actions) && item.actions.length > 0);
            const logisticsPath = !hasAnyAction ? logisticsNotificationPath(item) : null;
            if (logisticsPath) {
                window.location.assign(logisticsPath);
                return;
            }

            await executeAction(item.primary_action);
        },
        [markAsRead, executeAction],
    );

    // Handle action button click
    const handleActionButtonClick = useCallback(
        async (e, action, item) => {
            e.stopPropagation();

            // Mark notification as read if not already read
            if (!item.is_read) {
                await markAsRead(item.id);
            }

            await executeAction(action);
        },
        [markAsRead, executeAction],
    );

    // STOMP Frame Builders
    const buildConnectFrame = (token) => {
        token = token.startsWith("Bearer ") ? token : "Bearer " + token;
        return `CONNECT\naccept-version:1.2\nAuthorization:${token}\n\n\x00`;
    };

    const buildSubscribeFrame = (userId) => {
        return `SUBSCRIBE\nid:sub-notifications\ndestination:/topic/notifications/${userId}\n\n\x00`;
    };

    // WebSocket connection setup
    useEffect(() => {
        const connectWebSocket = () => {
            const config = _getConfig();
            let token = config?.headers?.Authorization || localStorage.getItem("token") || sessionStorage.getItem("token") || _getCookieValue("token");
            const userId = getUserData()?.id;

            if (!token || !userId) {
                console.log("No token or user ID available, cannot connect to notification WebSocket");
                return;
            }

            // Clean up existing connection if any
            if (wsRef.current) {
                wsRef.current.close();
            }

            const wsUrl = `${getBaseUrl("notes").replace("https://", "wss://")}/ws/websocket`;
            const newWs = new WebSocket(wsUrl);

            newWs.onopen = () => {
                console.log("Notification WebSocket opened");
                setIsWsConnected(true);
                connectionAttemptsRef.current = 0;

                try {
                    const connectFrame = buildConnectFrame(token);
                    newWs.send(connectFrame);

                    setTimeout(() => {
                        try {
                            const subscribeFrame = buildSubscribeFrame(userId);
                            newWs.send(subscribeFrame);
                            console.log("Subscribed to notification topic");
                        } catch (error) {
                            console.error("Error sending SUBSCRIBE frame:", error);
                        }
                    }, 1000);
                } catch (error) {
                    console.error("Error sending CONNECT frame:", error);
                }
            };

            newWs.onmessage = (event) => {
                const message = event.data;

                console.log("Notification WebSocket message received:", message);

                if (message.includes("MESSAGE")) {
                    try {
                        const bodyStart = message.indexOf("\n\n") + 2;
                        const bodyEnd = message.lastIndexOf("\x00");
                        const jsonBody = message.substring(bodyStart, bodyEnd);
                        const hookJson = JSON.parse(jsonBody);

                        console.log("Parsed notification:", hookJson);

                        const notification = JSON.parse(hookJson?.content || "{}");

                        console.log("Notification content parsed:", notification);

                        const normalizedNotification = {
                            ...notification,
                            timestamp: notification.timestamp || notification.created_at || notification.createdAt || notification.updatedAt || notification.updated_at || Date.now(),
                        };

                        // Add new notification to the list (only if not duplicate)
                        setNotifications((prev) => {
                            const uniqueNotifications = deduplicateNotifications([normalizedNotification], prev);
                            return [...uniqueNotifications, ...prev];
                        });

                        // Increase unread count if notification is unread
                        if (!normalizedNotification.is_read) {
                            setUnreadCount((prev) => prev + 1);
                        }

                        // Play notification sound
                        playNotificationSound();

                        // toast.success("New notification received", {
                        //     position: "top-right",
                        //     duration: 2000,
                        //     // closeButton: true,
                        //     // onClick: () => openPanel(),
                        //     style: { cursor: "pointer" },
                        //     action: (
                        //         <div onClick={openPanel} className="notification-toast-action">
                        //             View Notifications
                        //         </div>
                        //     ),
                        //     icon: <NotificationBell color="#027402" />,
                        // });

                        notificationToast({
                            item: normalizedNotification,
                            handleNotificationClick,
                            handleActionButtonClick,
                        });
                    } catch (error) {
                        console.error("Error parsing notification message:", error);
                    }
                }
            };

            newWs.onclose = (event) => {
                console.log("Notification WebSocket closed", event.code);
                setIsWsConnected(false);

                if (!event.wasClean && event.code !== 1000 && connectionAttemptsRef.current < 10) {
                    const delay = 1000 * Math.pow(2, connectionAttemptsRef.current);
                    reconnectTimeoutRef.current = setTimeout(() => {
                        connectionAttemptsRef.current += 1;
                        connectWebSocket();
                    }, delay);
                }
            };

            newWs.onerror = (error) => {
                console.error("Notification WebSocket error:", error);
                setIsWsConnected(false);
            };

            wsRef.current = newWs;
        };

        connectWebSocket();

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
        };
    }, [playNotificationSound, openPanel, handleNotificationClick, handleActionButtonClick]);

    // Fetch initial data on mount
    useEffect(() => {
        fetchUnreadCount();
        fetchNotifications();
    }, [fetchUnreadCount, fetchNotifications]);

    const value = {
        notifications,
        setNotifications,
        unreadCount,
        setUnreadCount,
        isLoading,
        isLoadingMore,
        isWsConnected,
        currentPage,
        totalPages,
        totalItems,
        hasMore,
        fetchNotifications,
        fetchMoreNotifications,
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        playNotificationSound,
        isPanelOpen,
        openPanel,
        closePanel,
        executeAction,
        handleNotificationClick,
        handleActionButtonClick,
    };

    return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};
